import React, { useCallback, useMemo, useContext, useState } from 'react'
import { useDispatch } from 'react-redux'
import useBoardingVisit from '../../../hooks/useBoardingVisit'
import { updatePetAsync } from '../../../actions/pets/pets.actions'
import { BOARDING_FIELDS } from './config'
import useDaycareVisit from '../../../hooks/useDaycareVisit'
import useSelectedHousehold from '../../../hooks/useSelectedHousehold'
import useDayRestrictions from '../../../hooks/useDayRestrictions'
import SectionFormContainer from '../components/SectionFormContainer'
import BoardingWizardContext from './BoardingWizardContext'
import { makeStyles, CircularProgress } from '@material-ui/core'
import { gray, purple } from '../../../utils/colors'
import {
	FONT_WEIGHTS,
	NON_BREAKING_SPACE,
	formatNumsToDollars,
	TIME_BLOCKS,
	STATUS_ID,
	calculatePromoAmountToApply,
	getPromoApplicationFlags,
	getVisitPromoServiceTargets,
	LINE_ITEM_TYPES,
	BOARDING,
	getApplicableAmountToApplyToLineItem,
	isSignatureStayPackageAddon,
	getComfortPlusOvernightRate,
	getSharedComfortPlusOvernightRate,
	getSharedKennelDogCounts
} from '../../../utils'
import { ButtonBase, MenuItem, Button } from '@material-ui/core'
import { MainButton } from '../../Button/MainButton'
import PaymentMethodCard from '../../Cards/Payments/PaymentMethodCard'
import NoFacilityCardWarning from '../../Common/NoFacilityCardWarning'
import DifferentFacilityBookingWarning from '../../Common/DifferentFacilityBookingWarning'
import useFacilityPaymentStatus from '../../../hooks/useFacilityPaymentStatus'
import CancellationPolicyDialog from './components/CancellationPolicyDialog'
import { showError } from '../../../actions/misc/misc.actions'
import MoreMenu from '../../Common/Menus/MoreMenu'
import EditAddOnsDialog from './components/EditAddOnsDialog'
import EditMealAddOnsDialog from './components/EditMealAddOnsDialog'
import RemoveEarlyDropoffDialog from './components/RemoveEarlyDropoffDialog'
import RemoveLatePickupDialog from './components/RemoveLatePickupDialog'
import RemoveFirstDayDaycareDialog from './components/RemoveFirstDayDaycareDialog'
import RemoveLastDayDaycareDialog from './components/RemoveLastDayDaycareDialog'
import EditOvernightUpgradesDialog from './components/EditOvernightUpgradesDialog'
import { WIZARD_SCREEN_TYPES } from '../constants'
import PromoOnlyEntryDialog from '../../Dialogs/Promos/PromoOnlyEntryDialog'
import usePromotions from '../../../hooks/usePromotions'
import * as Constants from '../../../utils/constants'
import StyledAlert from '../../Common/StyledAlert'

const useStyles = makeStyles(() => ({
	root: {
		flex: 1,
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		alignSelf: 'center',
		gap: '1rem',
		padding: '0 1rem',
		position: 'relative'
	},
	invoiceContainer: {
		display: 'flex',
		backgroundColor: gray[300],
		width: '70%',
		borderRadius: '0.375rem',
		flexDirection: 'column',
		padding: '1.4rem'
	},
	invoiceHeader: {
		marginBottom: '1.5rem'
	},
	emphasizedText: {
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		fontSize: '1rem',
		color: gray[900]
	},
	multiLineRowText: { display: 'flex', flexDirection: 'column', lineHeight: '1.1rem' },
	subText: {
		fontSize: '.875rem',
		color: gray[700],
		fontWeight: FONT_WEIGHTS.SemiBold
	},
	mealText: {
		fontSize: '.875rem',
		color: gray[700],
		fontStyle: 'italic'
	},
	actionsContainer: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		'& .MuiCard-root': {
			boxShadow: 'none',
			'& .MuiCardHeader-root': {
				'& .MuiCardHeader-content': {
					'& .MuiCardHeader-title': {
						textTransform: 'unset'
					}
				}
			}
		}
	},
	actionButtonContainer: {
		display: 'flex',
		flexDirection: 'column',
		padding: '1rem',
		gap: '1rem'
	},
	disclaimerText: {
		textAlign: 'center',
		fontFamily: 'Nunito',
		fontSize: '0.875rem',
		padding: '0 0.5rem',
		fontWeight: FONT_WEIGHTS.SemiBold,
		'& span': {
			fontWeight: FONT_WEIGHTS.ExtraBold,
			color: purple[500],
			cursor: 'pointer'
		}
	},
	activityOverlay: {
		position: 'absolute',
		zIndex: 200,
		backgroundColor: 'rgba(255,255,255,.8)',
		width: 'calc(100% - 2rem)',
		height: '100%',
		display: 'flex',
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center'
	},
	divider: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		marginBottom: '1rem',
		'& div:nth-child(1)': {
			height: '1px',
			borderBottom: `1px solid ${gray[500]}`,
			flex: 1
		}
	},
	spacer: {
		flex: 1
	},
	inlineButtons: {
		fontFamily: 'Nunito',
		fontSize: '0.875rem',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		color: purple[500]
	},
	addPromoButton: {
		color: purple[500],
		fontSize: '0.875rem',
		letterSpacing: 1.25,
		padding: 0,
		marginTop: '1.25rem'
	}
}))

const useRowStyles = makeStyles(() => ({
	row: {
		display: 'flex',
		flex: 1,
		gap: '.9rem',
		alignItems: 'center',
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.SemiBold,
		fontSize: '1rem',
		color: gray[900],
		width: '100%',
		'& div:nth-child(1)': {
			width: '8.5rem'
		},
		'& div:nth-child(2)': {
			width: '1.6rem'
		},
		'& div:nth-child(3)': {
			width: '5.2125rem'
		},
		'& div:nth-child(4)': {
			width: '5.3rem'
		},
		'& div:nth-child(5)': {
			width: '4.7rem'
		},
		'& div:nth-child(6)': {
			width: '1.5rem'
		}
	},
	header: {
		borderBottom: `1px solid ${gray[500]}`,
		height: '2rem',
		fontWeight: FONT_WEIGHTS.SemiBold,
		fontSize: '0.875rem',
		flex: 'unset',
		marginBottom: '0.3125rem'
	}
}))

const useTableStyles = ({ scrollable }) =>
	makeStyles(() => {
		const styles = {
			table: {
				display: 'flex',
				flexDirection: 'column',
				gap: '.8rem'
			}
		}
		if (scrollable) {
			styles.table['overflow'] = 'scroll'
			styles.table['maxHeight'] = '32rem'
			styles.table['marginBottom'] = '1rem'
		}
		return styles
	})

const Row = ({ children, header = false }) => {
	const classes = useRowStyles()
	return <div className={`${classes.row} ${header ? classes.header : null}`}>{children}</div>
}

const Header = ({ children }) => {
	return <Row header>{children}</Row>
}

const FakeTable = ({ children, scrollable }) => {
	const classes = useTableStyles({ scrollable })()
	return <div className={classes.table}>{children}</div>
}

const ServiceSummaryAndPayment = (props) => {
	const dispatch = useDispatch()
	const {
		wizardContextProvider: { wizard_state, setWizardAttributeAndValues }
	} = useContext(BoardingWizardContext)

	const classes = useStyles()
	const { facility, boarding, updateAddOns, update, updating } = useBoardingVisit()
	const { daycare, prices: daycarePrices } = useDaycareVisit()
	const {
		addOnBankValue,
		hasValidCardOnFile,
		household,
		daycareCredits,
		isVip,
		isPetCare,
		totalCreditAvailable
	} = useSelectedHousehold()

	// Cards are saved per facility. The wizard creates a Stripe customer for this
	// facility and will not confirm until a card exists on that facility's instance.
	const facilityCardStatus = useFacilityPaymentStatus(household?._id, facility?._id, { ensureCustomer: true })
	const hasCardAtThisFacility =
		facilityCardStatus.hasPaymentMethod === true ||
		(facilityCardStatus.hasPaymentMethod == null &&
			String(facility?._id || '') === String(household?.default_facility_id || '') &&
			hasValidCardOnFile)
	const { dayRestrictionsMap } = useDayRestrictions()
	const { deletePromoForFacilityVisit } = usePromotions()

	const { goToStep } = props

	const [cancellationPolicyOpen, setCancellationPolicyOpen] = useState(false)
	const [editItemsDialogOpen, setEditItemsDialogOpen] = useState(false)
	const [editMealAddOnsDialogOpen, setEditMealAddOnsDialogOpen] = useState(false)
	const [editOvernightUpgradesDialogOpen, setEditOvernightUpgradesDialogOpen] = useState(false)
	const [removeEarlyDropoffDialogOpen, setRemoveEarlyDropoffDialogOpen] = useState(false)
	const [removeLatePickupDialogOpen, setRemoveLatePickupDialogOpen] = useState(false)
	const [removeFirstDayDaycareDialogOpen, setRemoveFirstDayDaycareDialogOpen] = useState(false)
	const [removeLastDayDaycareDialogOpen, setRemoveLastDayDaycareDialogOpen] = useState(false)
	const [promoEntryDialogOpen, setPromoEntryDialogOpen] = useState(false)
	const [addonIdToEdit, setAddonIdToEdit] = useState('')
	const [addonChoiceIdToEdit, setAddonChoiceIdToEdit] = useState('')
	const [editAddOnTitle, setEditAddonTitle] = useState('')
	const [foodAddOnsToEdit, setFoodAddOnsToEdit] = useState({
		pet_id: '',
		meals: [],
		name: '',
		image_url: ''
	})
	const [showBankCreditDisclaimer, setShowBankCreditDisclaimer] = useState(false)

	const numberOfPets = useMemo(() => wizard_state['pets'].length, [wizard_state])
	const visitId = useMemo(() => wizard_state['_id'], [wizard_state])

	const depositRequired = useMemo(() => {
		const { dates } = wizard_state

		let required = false

		for (const date of dates) {
			const restrictions = dayRestrictionsMap.get(date)
			if (restrictions && restrictions.boarding && restrictions.boarding.length) {
				required = restrictions.boarding.some((restriction) => restriction.deposit_required)
			}
		}

		return required
	}, [facility, wizard_state, dayRestrictionsMap])

	const firstDay = useMemo(() => {
		const { dates } = wizard_state
		const [day] = dates
		return day || null
	}, [wizard_state])

	const lastDay = useMemo(() => {
		const { dates } = wizard_state
		const lastDay = dates[dates.length - 1]
		return lastDay || null
	}, [wizard_state])

	const numberOfNights = useMemo(() => {
		const { dates } = wizard_state
		return dates.length - 1
	}, [wizard_state])

	const depositAmount = useMemo(() => {
		if (!boarding || !boarding.deposit_rate) {
			return 0
		}

		const { deposit_rate } = boarding
		const deposit = deposit_rate * numberOfNights * numberOfPets

		return deposit
	}, [boarding, numberOfNights, numberOfPets])

	const nonMealAddons = useMemo(() => {
		const { add_on_services } = wizard_state
		return add_on_services.filter((addon) => addon.category !== 'meal')
	}, [facility, wizard_state])

	const daycareFirstDay = useMemo(() => {
		const { first_day_daycare } = wizard_state
		return first_day_daycare
	}, [wizard_state])

	const daycareLastDay = useMemo(() => {
		const { last_day_daycare } = wizard_state
		return last_day_daycare
	}, [wizard_state])

	const daycareCreditsAvailable = useMemo(() => {
		let available = 0
		const credits = daycareCredits.filter((c) => !c.expired && !c.used && c.credit_remaining > 0)

		if (credits.length) {
			available = credits.reduce((acc, cred) => acc + cred.credit_remaining, 0)
		}

		let creditsNeeded = numberOfPets * (numberOfNights - 1)

		if (daycareFirstDay) {
			creditsNeeded += numberOfPets
		}

		if (daycareLastDay) {
			creditsNeeded += numberOfPets
		}

		if (available > creditsNeeded) {
			return creditsNeeded
		}
		return available
	}, [wizard_state, numberOfPets, daycareCredits, daycareFirstDay, daycareLastDay, numberOfNights])

	const hasMorningMealFirstDay = useMemo(() => {
		const { add_on_services } = wizard_state

		// There maybe more than one morning meal for multiple pets, but we only need to find one
		// to know that morning meals have been selected
		const meal = add_on_services.some(
			(addon) => addon.category === 'meal' && addon.time === 'morning' && addon.date === firstDay
		)

		return meal || false
	}, [wizard_state, firstDay])

	const hasMiddayMealFirstDay = useMemo(() => {
		const { add_on_services } = wizard_state

		const meal = add_on_services.some(
			(addon) => addon.category === 'meal' && addon.time === TIME_BLOCKS.Midday && addon.date === firstDay
		)

		return meal || false
	}, [wizard_state, firstDay])

	const hasEveningMealLastDay = useMemo(() => {
		const { add_on_services } = wizard_state

		// There maybe more than one evening meal for multiple pets, but we only need to find one
		// to know that evening meals have been selected
		const meal = add_on_services.some(
			(addon) => addon.category === 'meal' && addon.time === TIME_BLOCKS.Evening && addon.date === lastDay
		)

		return meal || false
	}, [wizard_state, lastDay])

	const hasMiddayMealLastDay = useMemo(() => {
		const { add_on_services } = wizard_state

		const meal = add_on_services.some(
			(addon) => addon.category === 'meal' && addon.time === TIME_BLOCKS.Midday && addon.date === lastDay
		)

		return meal || false
	}, [wizard_state, lastDay])

	const handleCancellationPolicyDialogState = useCallback(() => {
		setCancellationPolicyOpen((prev) => !prev)
	}, [setCancellationPolicyOpen])

	const handleRemoveEarlyDropoffDialogState = useCallback(() => {
		setRemoveEarlyDropoffDialogOpen((prev) => !prev)
	}, [setRemoveEarlyDropoffDialogOpen])

	const handleRemoveFirstDayDaycareDialogState = useCallback(() => {
		setRemoveFirstDayDaycareDialogOpen((prev) => !prev)
	}, [setRemoveFirstDayDaycareDialogOpen])

	const handleRemoveLastDayDaycareDialogState = useCallback(() => {
		setRemoveLastDayDaycareDialogOpen((prev) => !prev)
	}, [setRemoveLastDayDaycareDialogOpen])

	const handleRemoveLatePickupDialogState = useCallback(() => {
		setRemoveLatePickupDialogOpen((prev) => !prev)
	}, [setRemoveLatePickupDialogOpen])

	const handleEditOvernightUpgradesDialogState = useCallback(() => {
		setEditOvernightUpgradesDialogOpen((prev) => !prev)
	}, [setEditOvernightUpgradesDialogOpen])

	const handlePromoEntryDialogState = useCallback(() => {
		setPromoEntryDialogOpen((prev) => !prev)
	}, [setPromoEntryDialogOpen])

	const handleRemoveEarlyDropoff = useCallback(async () => {
		handleRemoveEarlyDropoffDialogState()
		const newValues = { ...wizard_state }
		const { pets = [] } = wizard_state
		newValues['early_dropoff'] = false

		if (hasMorningMealFirstDay) {
			const { add_on_services } = wizard_state
			const newAddOnArray = add_on_services.filter((addon) => {
				if (addon.category === 'meal' && addon.time === 'morning' && addon.date === firstDay) {
					return false
				}
				return true
			})

			newValues['add_on_services'] = newAddOnArray
		}

		setWizardAttributeAndValues(newValues)
		if (hasMorningMealFirstDay) {
			const petIds = pets.map((a) => a._id)
			await updateAddOns({ visitId, addons: newValues['add_on_services'], petIds })
		}
		await update({ visitId, updates: { early_dropoff: false } })
	}, [
		wizard_state,
		setWizardAttributeAndValues,
		update,
		visitId,
		hasMorningMealFirstDay,
		handleRemoveEarlyDropoffDialogState,
		firstDay,
		updateAddOns
	])

	const handleRemoveLatePickup = useCallback(async () => {
		handleRemoveLatePickupDialogState()
		const newValues = { ...wizard_state }
		const { pets = [] } = wizard_state
		newValues['extended_pickup'] = false

		if (hasEveningMealLastDay) {
			const { add_on_services } = wizard_state
			const newAddOnArray = add_on_services.filter((addon) => {
				if (addon.category === 'meal' && addon.time === TIME_BLOCKS.Evening && addon.date === lastDay) {
					return false
				}
				return true
			})
			newValues['add_on_services'] = newAddOnArray
		}

		setWizardAttributeAndValues(newValues)
		if (hasEveningMealLastDay) {
			const petIds = pets.map((a) => a._id)
			await updateAddOns({ visitId, addons: newValues['add_on_services'], petIds })
		}
		await update({ visitId, updates: { extended_pickup: false } })
	}, [
		wizard_state,
		setWizardAttributeAndValues,
		update,
		visitId,
		hasEveningMealLastDay,
		handleRemoveLatePickupDialogState,
		lastDay,
		updateAddOns
	])

	const handleRemoveFirstDayDaycare = useCallback(async () => {
		handleRemoveFirstDayDaycareDialogState()
		const newValues = { ...wizard_state }
		const { pets = [] } = wizard_state

		if (hasMorningMealFirstDay || hasMiddayMealFirstDay) {
			const { add_on_services } = wizard_state
			const newAddOnArray = add_on_services.filter((addon) => {
				if (addon.category === 'meal' && addon.time === 'morning' && addon.date === firstDay) {
					return false
				}
				if (addon.category === 'meal' && addon.time === TIME_BLOCKS.Midday && addon.date === firstDay) {
					return false
				}
				return true
			})
			newValues['add_on_services'] = newAddOnArray
		}

		setWizardAttributeAndValues(newValues)
		if (hasMorningMealFirstDay || hasMiddayMealFirstDay) {
			const petIds = pets.map((a) => a._id)
			await updateAddOns({ visitId, addons: newValues['add_on_services'], petIds })
		}
		await update({ visitId, updates: { first_day_daycare: false, early_dropoff: false } })
	}, [
		wizard_state,
		setWizardAttributeAndValues,
		update,
		visitId,
		handleRemoveFirstDayDaycareDialogState,
		hasMiddayMealFirstDay,
		hasMorningMealFirstDay,
		firstDay,
		updateAddOns
	])

	const handleRemoveLastDayDaycare = useCallback(async () => {
		handleRemoveLastDayDaycareDialogState()
		const newValues = { ...wizard_state }
		const { pets = [] } = wizard_state

		if (hasEveningMealLastDay || hasMiddayMealLastDay) {
			const { add_on_services } = wizard_state
			const newAddOnArray = add_on_services.filter((addon) => {
				if (addon.category === 'meal' && addon.time === TIME_BLOCKS.Evening && addon.date === lastDay) {
					return false
				}
				if (addon.category === 'meal' && addon.time === TIME_BLOCKS.Midday && addon.date === lastDay) {
					return false
				}
				return true
			})
			newValues['add_on_services'] = newAddOnArray
		}

		setWizardAttributeAndValues(newValues)
		if (hasEveningMealLastDay || hasMiddayMealLastDay) {
			const petIds = pets.map((a) => a._id)
			await updateAddOns({ visitId, addons: newValues['add_on_services'], petIds })
		}
		await update({ visitId, updates: { last_day_daycare: false, extended_pickup: false } })
	}, [
		wizard_state,
		setWizardAttributeAndValues,
		update,
		visitId,
		handleRemoveLastDayDaycareDialogState,
		hasEveningMealLastDay,
		hasMiddayMealLastDay,
		lastDay,
		updateAddOns
	])

	const handleRemovePromo = useCallback(async () => {
		const { applied_promo, _id, household_id } = wizard_state
		if (!applied_promo) {
			return
		}

		await deletePromoForFacilityVisit({
			promo_code: applied_promo.code,
			promo_id: applied_promo._id,
			visit_id: _id,
			household_id
		})
	}, [deletePromoForFacilityVisit, wizard_state])

	const handleEditAddOns = useCallback(
		({ addon_id, addon_choice_id = '' }, title) => {
			setEditItemsDialogOpen(true)
			setAddonIdToEdit(addon_id)
			setAddonChoiceIdToEdit(addon_choice_id)
			setEditAddonTitle(title)
		},
		[setEditItemsDialogOpen, setAddonIdToEdit, setAddonChoiceIdToEdit]
	)

	const handleEditMealAddons = useCallback(
		({ pet_id, meals, name, image_url }) => {
			const mealTimeBlocks = new Set()
			for (const meal of meals) {
				if (Array.isArray(meal)) {
					const [, timeblock] = meal
					mealTimeBlocks.add(timeblock)
				} else {
					mealTimeBlocks.add(meal)
				}
			}
			setFoodAddOnsToEdit({ pet_id, meals: [...mealTimeBlocks.values()], name, image_url })
			setEditMealAddOnsDialogOpen(true)
		},
		[setFoodAddOnsToEdit]
	)

	const handleRemoveAddOn = useCallback(
		async ({ addon, forAll = false }) => {
			const newValues = { ...wizard_state }
			const { pets = [] } = wizard_state
			let newAddOnArray = []

			if (!addon && forAll) {
				setEditItemsDialogOpen(false)
				newAddOnArray = newValues['add_on_services'].filter((a) => {
					if (a.category === 'meal') {
						return true
					}
					if (a.addon_id === addonIdToEdit) {
						if (addonChoiceIdToEdit > '' && a.addon_choice_id !== addonChoiceIdToEdit) {
							return true
						}
						return false
					}
					return true
				})
			} else {
				// Not implemented yet
			}

			newValues['add_on_services'] = newAddOnArray
			setWizardAttributeAndValues(newValues)
			const petIds = pets.map((a) => a._id)
			await updateAddOns({ visitId, addons: newValues['add_on_services'], petIds })

			setAddonIdToEdit('')
			setAddonChoiceIdToEdit('')
		},
		[
			wizard_state,
			setWizardAttributeAndValues,
			addonChoiceIdToEdit,
			addonIdToEdit,
			updateAddOns,
			visitId,
			setAddonIdToEdit,
			setAddonChoiceIdToEdit
		]
	)

	const handleUpdateFoodAddons = useCallback(
		async ({ pet_id, meals }) => {
			setEditMealAddOnsDialogOpen(false)
			const { pets } = wizard_state

			await updatePetAsync(dispatch, pet_id, { food_schedule: meals })

			const updatedPets = pets.map((pet) =>
				pet._id === pet_id ? { ...pet, food_schedule: meals } : pet
			)

			setWizardAttributeAndValues({
				...wizard_state,
				[BOARDING_FIELDS.pets]: updatedPets,
				[BOARDING_FIELDS.pet_food_schedules]: {
					...wizard_state[BOARDING_FIELDS.pet_food_schedules],
					[pet_id]: meals
				}
			})
			setFoodAddOnsToEdit({
				pet_id: '',
				meals: [],
				name: '',
				image_url: ''
			})
		},
		[dispatch, wizard_state, setWizardAttributeAndValues, setEditMealAddOnsDialogOpen, setFoodAddOnsToEdit]
	)

	const handleUpdateAddons = useCallback(
		async (revisedAddons) => {
			setEditItemsDialogOpen(false)
			const newValues = { ...wizard_state }
			const { pets = [] } = wizard_state
			newValues['add_on_services'] = [...revisedAddons]
			setWizardAttributeAndValues(newValues)
			const petIds = pets.map((a) => a._id)
			await updateAddOns({ visitId, addons: newValues['add_on_services'], petIds })

			setAddonIdToEdit('')
			setAddonChoiceIdToEdit('')
		},
		[
			wizard_state,
			setWizardAttributeAndValues,
			setEditItemsDialogOpen,
			setAddonIdToEdit,
			setAddonChoiceIdToEdit,
			visitId,
			updateAddOns
		]
	)

	const daycareServiceCost = useMemo(() => {
		let price = -1

		if (daycarePrices) {
			const cost = daycarePrices['1']
			price = cost
		}

		return price
	}, [daycarePrices])

	const boardingServiceLineItem = useMemo(() => {
		const lineItems = []

		if (!boarding) {
			return lineItems
		}

		const { sharing_kennels, boarding_options = [] } = wizard_state
		const { overnights_price, overnights_price_shared, comfort_plus_overnights_price, comfort_upgrade_sharing_discount } =
			boarding
		const boardingRates = {
			overnights_price,
			comfort_plus_overnights_price,
			comfort_upgrade_sharing_discount
		}
		const comfortPlusRate = getComfortPlusOvernightRate(boardingRates)
		const sharedComfortPlusRate = getSharedComfortPlusOvernightRate(boardingRates)
		const comfortPlusCount = boarding_options.filter(
			(option) => option.stay_type === 'comfort_plus'
		).length
		const hasComfortPlus = comfortPlusCount > 0
		const allComfortPlus = hasComfortPlus && comfortPlusCount === numberOfPets
		const standardCount = Math.max(numberOfPets - comfortPlusCount, 0)
		const { pairedDogs, oddDogs } = getSharedKennelDogCounts(numberOfPets)

		const singleDescription = (
			<div className={classes.multiLineRowText}>
				Overnights
				<span className={classes.subText}>(Single)</span>
			</div>
		)

		const sharedDescription = (
			<div className={classes.multiLineRowText}>
				Overnights
				<span className={classes.subText}>(Shared)</span>
			</div>
		)

		const standardDescription = (
			<div className={classes.multiLineRowText}>
				Overnights
				<span className={classes.subText}>(Standard)</span>
			</div>
		)

		const comfortPlusDescription = (
			<div className={classes.multiLineRowText}>
				Overnights
				<span className={classes.subText}>(Signature Stay)</span>
			</div>
		)

		const comfortPlusSharedDescription = (
			<div className={classes.multiLineRowText}>
				Overnights
				<span className={classes.subText}>(Signature Stay Shared)</span>
			</div>
		)

		const comfortPlusSoloDescription = (
			<div className={classes.multiLineRowText}>
				Overnights
				<span className={classes.subText}>(Signature Stay · 1 Dog)</span>
			</div>
		)

		if (sharing_kennels && !hasComfortPlus) {
			const singleKennelRequiredForOneDog = numberOfPets % 2 !== 0
			const numberOfSharedKennels = Math.floor(numberOfPets / 2)
			const sharedQuantity = numberOfNights * numberOfSharedKennels

			lineItems.push({
				description: sharedDescription,
				tooltipDescription: 'Overnights',
				quantity: sharedQuantity,
				upcharge: formatNumsToDollars(0),
				price: overnights_price_shared,
				totalPrice: sharedQuantity * overnights_price_shared,
				formattedPrice: formatNumsToDollars(overnights_price_shared),
				formattedQuantity: `${sharedQuantity}x`,
				formattedTotalPrice: formatNumsToDollars(sharedQuantity * overnights_price_shared),
				formattedUpcharge: formatNumsToDollars(0)
			})
			if (singleKennelRequiredForOneDog) {
				lineItems.push({
					description: singleDescription,
					tooltipDescription: 'Overnights',
					quantity: numberOfNights,
					upcharge: formatNumsToDollars(0),
					price: overnights_price,
					totalPrice: numberOfNights * overnights_price,
					formattedPrice: formatNumsToDollars(overnights_price),
					formattedQuantity: `${numberOfNights}x`,
					formattedTotalPrice: formatNumsToDollars(numberOfNights * overnights_price),
					formattedUpcharge: formatNumsToDollars(0)
				})
			}
		} else if (sharing_kennels && allComfortPlus) {
			if (pairedDogs > 0) {
				const quantity = numberOfNights * pairedDogs
				const totalPrice = sharedComfortPlusRate * quantity
				lineItems.push({
					description: comfortPlusSharedDescription,
					tooltipDescription: 'Signature Stay (Shared)',
					quantity,
					upcharge: formatNumsToDollars(0),
					price: sharedComfortPlusRate,
					totalPrice,
					formattedPrice: formatNumsToDollars(sharedComfortPlusRate),
					formattedQuantity: `${quantity}x`,
					formattedTotalPrice: formatNumsToDollars(totalPrice),
					formattedUpcharge: formatNumsToDollars(0)
				})
			}
			if (oddDogs > 0) {
				const quantity = numberOfNights * oddDogs
				const totalPrice = comfortPlusRate * quantity
				lineItems.push({
					description: comfortPlusSoloDescription,
					tooltipDescription: 'Signature Stay (1 Dog)',
					quantity,
					upcharge: formatNumsToDollars(0),
					price: comfortPlusRate,
					totalPrice,
					formattedPrice: formatNumsToDollars(comfortPlusRate),
					formattedQuantity: `${quantity}x`,
					formattedTotalPrice: formatNumsToDollars(totalPrice),
					formattedUpcharge: formatNumsToDollars(0)
				})
			}
		} else if (hasComfortPlus) {
			if (standardCount > 0) {
				const quantity = numberOfNights * standardCount
				const totalPrice = overnights_price * quantity
				lineItems.push({
					description: standardDescription,
					tooltipDescription: 'Standard Boarding',
					quantity,
					upcharge: formatNumsToDollars(0),
					price: overnights_price,
					totalPrice,
					formattedPrice: formatNumsToDollars(overnights_price),
					formattedQuantity: `${quantity}x`,
					formattedTotalPrice: formatNumsToDollars(totalPrice),
					formattedUpcharge: formatNumsToDollars(0)
				})
			}
			const comfortQuantity = numberOfNights * comfortPlusCount
			const comfortTotal = comfortPlusRate * comfortQuantity
			lineItems.push({
				description: comfortPlusDescription,
				tooltipDescription: 'Signature Stay',
				quantity: comfortQuantity,
				upcharge: formatNumsToDollars(0),
				price: comfortPlusRate,
				totalPrice: comfortTotal,
				formattedPrice: formatNumsToDollars(comfortPlusRate),
				formattedQuantity: `${comfortQuantity}x`,
				formattedTotalPrice: formatNumsToDollars(comfortTotal),
				formattedUpcharge: formatNumsToDollars(0)
			})
		} else {
			const quantity = numberOfNights * numberOfPets
			const totalPrice = overnights_price * quantity

			lineItems.push({
				description: singleDescription,
				tooltipDescription: 'Overnights',
				quantity,
				upcharge: formatNumsToDollars(0),
				price: overnights_price,
				totalPrice,
				formattedPrice: formatNumsToDollars(overnights_price),
				formattedQuantity: `${quantity}x`,
				formattedTotalPrice: formatNumsToDollars(totalPrice),
				formattedUpcharge: formatNumsToDollars(0)
			})
		}

		return lineItems
	}, [numberOfPets, numberOfNights, wizard_state, boarding, classes])

	const kennelOptionsLineItems = useMemo(() => {
		const lineItems = []

		if (!facility) {
			return lineItems
		}

		const { sharing_kennels, boarding_options = [], pets } = wizard_state
		const numberOfLuxuryKennels = boarding_options.filter((option) => option.kennel_type === 'Luxury')

		if (numberOfLuxuryKennels.length === 0) {
			return lineItems
		}

		const { kennel_options = [] } = facility
		const prices = {}
		kennel_options.forEach(({ type, price }) => {
			prices[type] = price
		})

		const luxuryPrice = prices['Luxury'] || -1
		let quantity = numberOfNights

		// if sharing kennels then only 1 line item and quantity is numberOfNights
		if (sharing_kennels) {
			const singleKennelRequiredForOneDog = numberOfPets % 2 !== 0
			const numberOfSharedKennels = Math.floor(numberOfPets / 2)
			const numberOfKennels = singleKennelRequiredForOneDog
				? numberOfSharedKennels + 1
				: numberOfSharedKennels
			const sharedQuantity = numberOfNights * numberOfKennels

			lineItems.push({
				description: (
					<div className={classes.multiLineRowText}>
						Overnight
						<span className={classes.subText}>Upgrades</span>
					</div>
				),
				tooltipDescription: 'Overnight Upgrades',
				quantity: sharedQuantity,
				upcharge: formatNumsToDollars(0),
				price: luxuryPrice,
				totalPrice: luxuryPrice * sharedQuantity,
				formattedPrice: formatNumsToDollars(luxuryPrice),
				formattedQuantity: `${sharedQuantity}x`,
				formattedTotalPrice: formatNumsToDollars(luxuryPrice * sharedQuantity),
				formattedUpcharge: formatNumsToDollars(0),
				options: [
					<MenuItem key="edit-shared-upgrades" onClick={handleEditOvernightUpgradesDialogState}>
						Edit
					</MenuItem>
				]
			})
		} else {
			// if not sharing but all have upgrades then 1 line item.
			if (numberOfLuxuryKennels === numberOfPets) {
				quantity = quantity * numberOfPets
				lineItems.push({
					description: (
						<div className={classes.multiLineRowText}>
							Overnight
							<span className={classes.subText}>Upgrade {props?.petNames || 'Doggos?'}</span>
						</div>
					),
					tooltipDescription: `Overnight Upgrade ${props?.petNames || 'Doggos?'}`,
					quantity,
					upcharge: formatNumsToDollars(0),
					price: luxuryPrice,
					totalPrice: luxuryPrice * quantity,
					formattedPrice: formatNumsToDollars(luxuryPrice),
					formattedQuantity: `${quantity}x`,
					formattedTotalPrice: formatNumsToDollars(luxuryPrice * quantity),
					formattedUpcharge: formatNumsToDollars(0),
					options: [
						<MenuItem
							key="edit-luxuruy-upgrades-multiple"
							onClick={handleEditOvernightUpgradesDialogState}>
							Remove
						</MenuItem>
					]
				})
			} else {
				for (const boarding_option of numberOfLuxuryKennels) {
					const { pet_id } = boarding_option
					const pet = pets.find(({ _id }) => _id === pet_id)

					lineItems.push({
						description: (
							<div className={classes.multiLineRowText}>
								Overnight
								<span className={classes.subText}>Upgrade {pet?.name || 'Doggo?'}</span>
							</div>
						),
						tooltipDescription: `Overnight Upgrade ${pet?.name || 'Doggo?'}`,
						quantity,
						upcharge: formatNumsToDollars(0),
						price: luxuryPrice,
						totalPrice: luxuryPrice * quantity,
						formattedPrice: formatNumsToDollars(luxuryPrice),
						formattedQuantity: `${quantity}x`,
						formattedTotalPrice: formatNumsToDollars(luxuryPrice * quantity),
						formattedUpcharge: formatNumsToDollars(0),
						options: [
							<MenuItem key="edit-upgrade" onClick={handleEditOvernightUpgradesDialogState}>
								Edit
							</MenuItem>
						]
					})
				}
			}
		}

		return lineItems
	}, [wizard_state, facility, numberOfNights, numberOfPets])

	const daycareLineItems = useMemo(() => {
		const lineItems = []

		const mandatoryDaysOfDaycare = numberOfPets * (numberOfNights - 1)

		lineItems.push({
			description: 'Daycare Days',
			tooltipDescription: 'Daycare Days',
			quantity: mandatoryDaysOfDaycare,
			upcharge: 0,
			price: 0,
			totalPrice: daycareServiceCost * mandatoryDaysOfDaycare,
			formattedPrice: '1 credit',
			formattedQuantity: `${mandatoryDaysOfDaycare}x`,
			formattedTotalPrice: formatNumsToDollars(daycareServiceCost * mandatoryDaysOfDaycare),
			formattedUpcharge: formatNumsToDollars(0)
		})

		if (daycareFirstDay) {
			lineItems.push({
				description: (
					<div className={classes.multiLineRowText}>
						Daycare
						<span className={classes.subText}>First Day</span>
					</div>
				),
				tooltipDescription: 'Daycare (First Day)',
				quantity: 1,
				upcharge: 0,
				price: 0,
				totalPrice: daycareServiceCost * numberOfPets,
				formattedPrice: '1 credit',
				formattedQuantity: `${numberOfPets}x`,
				formattedTotalPrice: formatNumsToDollars(daycareServiceCost * numberOfPets),
				formattedUpcharge: formatNumsToDollars(0),
				options: [
					<MenuItem key="remove-first-day-daycare" onClick={handleRemoveFirstDayDaycareDialogState}>
						Remove
					</MenuItem>
				]
			})
		}
		if (daycareLastDay) {
			lineItems.push({
				description: (
					<div className={classes.multiLineRowText}>
						Daycare
						<span className={classes.subText}>Last Day</span>
					</div>
				),
				tooltipDescription: 'Daycare (Last Day)',
				quantity: 1,
				upcharge: 0,
				price: 0,
				totalPrice: daycareServiceCost * numberOfPets,
				formattedPrice: '1 credit',
				formattedQuantity: `${numberOfPets}x`,
				formattedTotalPrice: formatNumsToDollars(daycareServiceCost * numberOfPets),
				formattedUpcharge: formatNumsToDollars(0),
				options: [
					<MenuItem key="remove-last-day-daycare" onClick={handleRemoveLastDayDaycareDialogState}>
						Remove
					</MenuItem>
				]
			})
		}

		return lineItems
	}, [daycareServiceCost, daycareCreditsAvailable, numberOfPets, daycareFirstDay, daycareLastDay])

	const daycareCreditBankLineItem = useMemo(() => {
		const totalPrice = daycareServiceCost * daycareCreditsAvailable
		return [
			{
				description: 'Credit Bank',
				tooltipDescription: 'Credit Bank',
				quantity: '',
				upcharge: formatNumsToDollars(0),
				price: `-${daycareCreditsAvailable} credits`,
				totalPrice: -totalPrice,
				formattedUpcharge: formatNumsToDollars(0),
				formattedTotalPrice: `-${formatNumsToDollars(totalPrice)}`,
				formattedPrice: `-${daycareCreditsAvailable} credits`
			}
		]
	}, [daycareServiceCost, daycareCreditsAvailable, numberOfPets])

	const addOnlineItems = useMemo(() => {
		const addOnMap = new Map()
		const { pets } = wizard_state

		if (facility) {
			const { addOnTypes } = facility || []

			nonMealAddons.forEach((addon, i) => {
				let price = 0
				let upcharge = 0
				let suboptionDescription = ''
				let currentPet = pets.find((pet) => pet._id === addon.pet_id)
				const {
					name,
					_id: rawId,
					addon_id: existingAddOnId,
					addon_choice_id = '',
					pet_id,
					training_service
				} = addon

				const addonKey = `${existingAddOnId || rawId}${addon_choice_id}`

				const masterAddon = addOnTypes.find(({ _id }) => {
					return _id === existingAddOnId || _id === rawId
				})

				const isPackageIncluded = isSignatureStayPackageAddon(addon, wizard_state, facility)

				if (masterAddon && !isPackageIncluded) {
					price = masterAddon.price
					if (addon_choice_id > '' && masterAddon.addon_choices) {
						const { upcharge: selectionUpcharge, name: selectionName } =
							masterAddon.addon_choices.find((choice) => {
								return choice.id === addon_choice_id
							}) || { upcharge: 0, name: '' }

						upcharge = selectionUpcharge
						suboptionDescription = selectionName
					}
				} else if (masterAddon && isPackageIncluded && addon_choice_id > '' && masterAddon.addon_choices) {
					const { name: selectionName } =
						masterAddon.addon_choices.find((choice) => {
							return choice.id === addon_choice_id
						}) || { name: '' }
					suboptionDescription = selectionName
				}

				const lineItem = {
					description: (
						<div className={classes.multiLineRowText}>
							{name}
							{suboptionDescription ? (
								<span className={classes.subText}>{suboptionDescription}</span>
							) : null}
						</div>
					),
					training_service,
					tooltipDescription: `${name}${suboptionDescription > '' ? ` - ${suboptionDescription}` : ''}`
				}

				if (!addOnMap.has(addonKey)) {
					const totalPrice = price + upcharge
					const formattedQuantity = `1x`
					const formattedPrice = price > -1 ? `${formatNumsToDollars(price)}` : '??'
					const formattedTotalPrice = totalPrice > -1 ? `${formatNumsToDollars(totalPrice)}` : '??'
					const formattedUpcharge = upcharge > -1 ? `${formatNumsToDollars(upcharge)}` : '??'

					const item = {
						...lineItem,
						quantity: 1,
						price,
						upcharge,
						totalPrice,
						formattedQuantity,
						formattedPrice,
						formattedUpcharge,
						formattedTotalPrice,
						options: [
							<MenuItem
								key={`edit-add-ons-${name}`}
								onClick={() => handleEditAddOns(addon, lineItem.tooltipDescription)}>
								Edit
							</MenuItem>
						]
					}

					addOnMap.set(addonKey, item)
				} else {
					const updatedItem = { ...addOnMap.get(addonKey) }
					const newQuantity = updatedItem.quantity + 1
					const newFormattedQuantity = `${newQuantity}x`
					const newPrice = updatedItem.price * newQuantity
					const newUpcharge = updatedItem.upcharge * newQuantity
					const newTotalPrice = newPrice + newUpcharge
					const newFormattedPrice = newTotalPrice > -1 ? `${formatNumsToDollars(newTotalPrice)}` : '??'

					updatedItem.quantity = newQuantity
					updatedItem.totalPrice = newTotalPrice
					updatedItem.formattedTotalPrice = newFormattedPrice
					updatedItem.formattedQuantity = newFormattedQuantity

					addOnMap.set(addonKey, updatedItem)
				}
			})
		}

		return [...addOnMap.values()]
	}, [wizard_state, nonMealAddons, facility])

	const pickupDropoffLineItems = useMemo(() => {
		const { early_dropoff, extended_pickup } = wizard_state
		const { early_dropoff_fee } = daycare || { early_dropoff_fee: 0, late_pickup_fee: 0 }
		const earlyDropoffPrice = isVip || isPetCare ? 0 : early_dropoff_fee
		const latePickupPrice = 0

		const lineItems = []

		if (early_dropoff) {
			lineItems.push({
				description: 'Early Drop-off',
				tooltipDescription: 'Early Drop-off',
				quantity: 1,
				upcharge: 0,
				price: earlyDropoffPrice,
				totalPrice: earlyDropoffPrice,
				formattedQuantity: '1x',
				formattedUpcharge: formatNumsToDollars(0),
				formattedTotalPrice: formatNumsToDollars(earlyDropoffPrice),
				formattedPrice: formatNumsToDollars(earlyDropoffPrice),
				options: [
					<MenuItem key="early-dropoff-remove" onClick={handleRemoveEarlyDropoffDialogState}>
						Remove
					</MenuItem>
				]
			})
		}
		if (extended_pickup) {
			lineItems.push({
				description: 'Late Pick-up',
				tooltipDescription: 'Late Pick-up',
				quantity: 1,
				upcharge: 0,
				price: latePickupPrice,
				totalPrice: latePickupPrice,
				formattedQuantity: '1x',
				formattedUpcharge: formatNumsToDollars(0),
				formattedTotalPrice: formatNumsToDollars(latePickupPrice),
				formattedPrice: formatNumsToDollars(latePickupPrice),
				options: [
					<MenuItem key="late-pickup-remove" onClick={handleRemoveLatePickupDialogState}>
						Remove
					</MenuItem>
				]
			})
		}

		return lineItems
	}, [wizard_state, daycare, isVip, isPetCare])

	const foodScheduleLineItems = useMemo(() => {
		const { pets = [] } = wizard_state
		const lineItems = []

		pets.forEach(({ _id: pet_id, name, image_url, food_schedule = [] }) => {
			lineItems.push({
				quantity: food_schedule.length,
				description: (
					<div className={classes.multiLineRowText}>
						Food Schedule
						<span className={classes.subText}>{name}</span>
					</div>
				),
				tooltipDescription: `Food Schedule - ${name}`,
				price: 0,
				upcharge: 0,
				totalPrice: 0,
				formattedQuantity: `${food_schedule.length}x`,
				formattedPrice: '',
				formattedUpcharge: '',
				formattedTotalPrice: '',
				options: [
					<MenuItem
						key="food-schedule-edit"
						onClick={() => handleEditMealAddons({ pet_id, meals: food_schedule, name, image_url })}>
						Edit
					</MenuItem>
				]
			})
		})

		return lineItems
	}, [wizard_state, handleEditMealAddons, classes])

	const holidaySurchargeLineItems = useMemo(() => {
		const lineItems = []
		const { dates } = wizard_state

		if (!dates || dates.length === 0) {
			return lineItems
		}

		let quantity = 0
		let surcharge = 0
		let totalPrice = 0
		let description = ''

		for (let x = 0; x < dates.length - 1; x++) {
			const date = dates[x]
			const holiday = dayRestrictionsMap.get(date)
			if (holiday && holiday.boarding && holiday.boarding.length > 0) {
				const { boarding } = holiday
				quantity += 1
				totalPrice += boarding[0].surchargeFee
				surcharge = boarding[0].surchargeFee
				description = boarding[0].name
			}
		}

		if (quantity > 0 && surcharge > 0) {
			lineItems.push({
				quantity,
				description: (
					<div className={classes.multiLineRowText}>
						Holiday surcharge
						<span className={classes.subText}>{description}</span>
					</div>
				),
				price: surcharge,
				upcharge: 0,
				totalPrice: totalPrice,
				formattedQuantity: `${quantity}x`,
				formattedPrice: formatNumsToDollars(surcharge),
				formattedUpcharge: formatNumsToDollars(0),
				formattedTotalPrice: formatNumsToDollars(totalPrice)
			})
		}

		return lineItems
	}, [wizard_state, dayRestrictionsMap])

	const totalPrice = useMemo(() => {
		let subtotal = 0
		for (const item of boardingServiceLineItem) {
			subtotal += item.totalPrice
		}
		for (const item of kennelOptionsLineItems) {
			subtotal += item.totalPrice
		}
		for (const item of daycareLineItems) {
			subtotal += item.totalPrice
		}
		for (const item of addOnlineItems) {
			subtotal += item.totalPrice
		}
		for (const item of pickupDropoffLineItems) {
			subtotal += item.totalPrice
		}
		for (const item of daycareCreditBankLineItem) {
			subtotal += item.totalPrice
		}
		for (const item of holidaySurchargeLineItems) {
			subtotal += item.totalPrice
		}

		return subtotal
	}, [
		boardingServiceLineItem,
		kennelOptionsLineItems,
		daycareLineItems,
		addOnlineItems,
		pickupDropoffLineItems,
		daycareCreditBankLineItem,
		holidaySurchargeLineItems
	])

	const appliedPromoAndAddonBankLineItems = useMemo(() => {
		const { applied_promo } = wizard_state

		const lineItems = []

		if (!applied_promo && addOnBankValue <= 0) {
			return lineItems
		}

		let remainingAddonBalance = addOnlineItems.reduce((acc, i) => acc + i.totalPrice, 0)
		let availableAddonBank = addOnBankValue || 0
		let remainingAddonBank = addOnBankValue || 0
		let addonBankAppliedInCents = 0
		let amountApplicableToPromo = 0
		let factorInBoardingNumberOfDays = false

		if (applied_promo) {
			const { code, value, unit } = applied_promo || {}
			const {
				appliesToBoarding,
				appliesToBoardingAddonsOnly,
				appliesToCredits,
				appliesToTraining,
				appliesToFVRT,
				appliesToVisitAddons
			} = getPromoApplicationFlags(applied_promo, BOARDING)

			const appliedPromoLineItem = {
				description: (
					<div>
						Applied Promo
						<br />
						<span className={classes.mealText}>{code}</span>
					</div>
				),
				tooltipDescription: code,
				quantity: 1,
				upcharge: 0,
				price: 0,
				totalPrice: 0,
				formattedQuantity: '',
				formattedUpcharge: '',
				formattedPrice: '',
				formattedTotalPrice: `-${formatNumsToDollars(0)}`,
				options: [
					<MenuItem key="edit-promo" onClick={handlePromoEntryDialogState}>
						Edit
					</MenuItem>,
					<MenuItem key="remove-promo" onClick={handleRemovePromo}>
						Remove
					</MenuItem>
				]
			}
			let daycareTotal = daycareLineItems.reduce((acc, i) => acc + i.totalPrice, 0)
			let boardingTotal = boardingServiceLineItem.reduce((acc, i) => acc + i.totalPrice, 0)
			let creditsTotal = 0

			let promoAmountAppliedToAddOnsInCents = 0

			// Need to handle $ based promos
			const promoIsCentBased = unit === Constants.CENTS
			const promoValue = value || 0

			let remainingPromoAmountInCents = promoIsCentBased ? promoValue : -1 // -1 helps us distinguish between $ and % based promos in the calculation logic

			if (daycareCreditBankLineItem.length > 0) {
				creditsTotal = daycareCreditBankLineItem.reduce((acc, i) => acc + i.totalPrice, 0)
			}

			if (appliesToCredits) {
				amountApplicableToPromo = boardingTotal + daycareTotal + creditsTotal
				factorInBoardingNumberOfDays = true
			} else if (appliesToBoarding) {
				amountApplicableToPromo = boardingTotal
				factorInBoardingNumberOfDays = true
			}

			const amountToApply = calculatePromoAmountToApply({
				promo: applied_promo,
				total: amountApplicableToPromo,
				visit: wizard_state,
				remainingPromoAmountInCents,
				factorInBoardingNumberOfDays
			})

			promoAmountAppliedToAddOnsInCents += -amountToApply
			if (promoIsCentBased) {
				remainingPromoAmountInCents += amountToApply
			}

			appliedPromoLineItem.price = promoAmountAppliedToAddOnsInCents * -1
			appliedPromoLineItem.totalPrice = promoAmountAppliedToAddOnsInCents * -1
			appliedPromoLineItem.formattedTotalPrice = `-${formatNumsToDollars(promoAmountAppliedToAddOnsInCents)}`
			appliedPromoLineItem.subtext = applied_promo.code
			appliedPromoLineItem.type = LINE_ITEM_TYPES.PreLineItemAppliedPromo

			if (addOnlineItems.length > 0 && appliesToVisitAddons) {
				for (const addon of addOnlineItems) {
						if (!addon.training_service && (appliesToFVRT || appliesToTraining)) {
							continue
						}

						if (promoIsCentBased) {
							if (promoValue > 0) {
								if (remainingPromoAmountInCents > 0) {
									const applicableAmount = getApplicableAmountToApplyToLineItem(
										addon.totalPrice,
										remainingPromoAmountInCents,
										promoAmountAppliedToAddOnsInCents
									)

									remainingAddonBalance -= applicableAmount.individualAmountApplied
									remainingPromoAmountInCents = applicableAmount.remainingBalance
									promoAmountAppliedToAddOnsInCents = applicableAmount.runningAmountApplied
								}
							}
						} else {
							const percentToInt = parseInt(promoValue, 10)
							const subtractedAmount = addon.totalPrice * (percentToInt / 100)
							remainingAddonBalance -= subtractedAmount
							promoAmountAppliedToAddOnsInCents += subtractedAmount
							remainingPromoAmountInCents = -1
						}
					}

				appliedPromoLineItem.price = promoAmountAppliedToAddOnsInCents * -1
				appliedPromoLineItem.totalPrice = promoAmountAppliedToAddOnsInCents * -1
				appliedPromoLineItem.formattedTotalPrice = `-${formatNumsToDollars(
					promoAmountAppliedToAddOnsInCents
				)}`
			}

			lineItems.push(appliedPromoLineItem)
		}

		const addonBankLineItem = {
			description: 'Enrichment Bank',
			quantity: 1,
			upcharge: 0,
			price: 0,
			totalPrice: 0,
			formattedQuantity: '',
			formattedUpcharge: '',
			formattedPrice: '',
			formattedTotalPrice: `-${formatNumsToDollars(0)}`
		}

		if (availableAddonBank > 0 && remainingAddonBalance > 0 && addOnlineItems.length > 0) {
			const applicableBankAmount = getApplicableAmountToApplyToLineItem(
				remainingAddonBalance,
				remainingAddonBank,
				addonBankAppliedInCents
			)
			remainingAddonBalance -= applicableBankAmount.individualAmountApplied
			remainingAddonBank = applicableBankAmount.remainingBalance
			addonBankAppliedInCents = applicableBankAmount.runningAmountApplied

			if (addonBankAppliedInCents > 0) {
				addonBankLineItem.price = addonBankAppliedInCents * -1
				addonBankLineItem.totalPrice = addonBankAppliedInCents * -1
				addonBankLineItem.formattedTotalPrice = `-${formatNumsToDollars(addonBankAppliedInCents)}`
				lineItems.push(addonBankLineItem)
				setShowBankCreditDisclaimer(true)
			}
		}

		return lineItems
	}, [
		wizard_state,
		totalPrice,
		boardingServiceLineItem,
		daycareLineItems,
		daycareCreditBankLineItem,
		addOnlineItems,
		pickupDropoffLineItems,
		addOnBankValue,
		setShowBankCreditDisclaimer
	])

	const availableAccountCreditLineItems = useMemo(() => {
		if (totalCreditAvailable <= 0) {
			return []
		}

		let total = totalPrice

		if (total <= 0) {
			return []
		}

		const items = []

		for (const item of appliedPromoAndAddonBankLineItems) {
			total += item.price
		}

		let price = total - totalCreditAvailable

		if (totalCreditAvailable > total || totalCreditAvailable === total) {
			price = total
		} else if (totalCreditAvailable < total) {
			price = totalCreditAvailable
		}

		setShowBankCreditDisclaimer(true)

		items.push({
			description: 'Account Credit',
			quantity: '',
			upcharge: formatNumsToDollars(0),
			price: `-${price} credits`,
			totalPrice: -price,
			formattedUpcharge: formatNumsToDollars(0),
			formattedTotalPrice: `-${formatNumsToDollars(price)}`,
			formattedPrice: `-${formatNumsToDollars(price)}`
		})

		return items
	}, [totalCreditAvailable, totalPrice, appliedPromoAndAddonBankLineItems, setShowBankCreditDisclaimer])

	const totalPriceWithPromo = useMemo(() => {
		let subtotal = totalPrice
		for (const item of appliedPromoAndAddonBankLineItems) {
			subtotal += item.totalPrice
		}
		for (const item of availableAccountCreditLineItems) {
			subtotal += item.totalPrice
		}
		return subtotal
	}, [totalPrice, wizard_state, appliedPromoAndAddonBankLineItems, availableAccountCreditLineItems])

	const depositLineItems = useMemo(() => {
		if (!depositRequired || !boarding || !boarding.deposit_rate) {
			return []
		}

		const { deposit_paid, applied_promo } = wizard_state

		const total = applied_promo !== null || totalCreditAvailable > 0 ? totalPriceWithPromo : totalPrice

		const totalDueLater = total - depositAmount

		return [
			{
				quantity: 1,
				description: <div>Total Price</div>,
				price: total,
				upcharge: 0,
				totalPrice,
				formattedQuantity: '',
				formattedPrice: '',
				formattedUpcharge: '',
				formattedTotalPrice: formatNumsToDollars(total)
			},
			{
				quantity: 1,
				description: (
					<div className={classes.multiLineRowText}>
						{deposit_paid ? 'Total Paid' : 'Total Due Now'}
						<span className={classes.subText}>Holiday Deposit</span>
					</div>
				),
				price: depositAmount,
				upcharge: 0,
				totalPrice: depositAmount,
				formattedQuantity: '',
				formattedPrice: '',
				formattedUpcharge: '',
				formattedTotalPrice: formatNumsToDollars(depositAmount)
			},
			{
				quantity: 1,
				description: <div>Total Due Later</div>,
				price: totalDueLater,
				upcharge: 0,
				totalPrice: totalDueLater,
				formattedQuantity: '',
				formattedPrice: '',
				formattedUpcharge: '',
				formattedTotalPrice: formatNumsToDollars(totalDueLater)
			}
		]
	}, [
		depositRequired,
		depositAmount,
		totalPrice,
		boarding,
		wizard_state,
		numberOfNights,
		numberOfPets,
		totalPriceWithPromo
	])

	const handleOnConfirmAndBook = useCallback(async () => {
		const updates = {
			status: { id: STATUS_ID.Approved, name: 'Approved' },
			require_facility_payment_method: true
		}

		const { status, deposit_paid } = wizard_state

		if (status.id === STATUS_ID.Pending && !deposit_paid && depositRequired) {
			updates['chargeDepositOnBoardingIfNotAlreadyPaid'] = depositRequired
		}

		const newValues = { ...wizard_state }
		newValues['status'] = updates.status

		setWizardAttributeAndValues(newValues)

		try {
			const response = await update({ visitId, updates })
			if (response && response.data) {
				const { data } = response
				if (data.visit && props.onComplete) {
					props.onComplete()
				}
			}
		} catch (e) {
			showError('Error Scheduling!')
		}
	}, [wizard_state, update, visitId, depositRequired, depositAmount])

	const handleUpdateOvernightUpgrades = useCallback(
		async ({ boarding_options, sharing_kennels }) => {
			setEditOvernightUpgradesDialogOpen(false)
			const newValues = { ...wizard_state }
			newValues['boarding_options'] = [...boarding_options]
			newValues['sharing_kennels'] = sharing_kennels
			setWizardAttributeAndValues(newValues)
			if (goToStep) {
				goToStep({ screenType: WIZARD_SCREEN_TYPES.Invoice })
			}
			await update({
				visitId,
				updates: { boarding_options, sharing_kennels }
			})
		},
		[wizard_state, setWizardAttributeAndValues, setEditOvernightUpgradesDialogOpen, visitId, goToStep]
	)

	const promoServiceTargets = useMemo(() => {
		return getVisitPromoServiceTargets(wizard_state)
	}, [wizard_state])

	return (
		<SectionFormContainer>
			<CancellationPolicyDialog
				open={cancellationPolicyOpen}
				onClose={handleCancellationPolicyDialogState}
				numPets={numberOfPets}
			/>
			<EditOvernightUpgradesDialog
				open={editOvernightUpgradesDialogOpen}
				onCancel={handleEditOvernightUpgradesDialogState}
				onSave={(updates) => handleUpdateOvernightUpgrades(updates)}
			/>
			<EditMealAddOnsDialog
				open={editMealAddOnsDialogOpen}
				onCancel={() => setEditMealAddOnsDialogOpen(false)}
				values={foodAddOnsToEdit}
				onSave={(updates) => handleUpdateFoodAddons(updates)}
				firstDay={firstDay}
				lastDay={lastDay}
				daycareFirstDay={daycareFirstDay}
				daycareLastDay={daycareLastDay}
				hasEarlyDropoff={wizard_state['early_dropoff']}
				hasLatePickup={wizard_state['extended_pickup']}
			/>
			<EditAddOnsDialog
				open={editItemsDialogOpen}
				title={editAddOnTitle}
				onDelete={() => handleRemoveAddOn({ forAll: true })}
				onSave={(revisedAddons) => handleUpdateAddons(revisedAddons)}
				onCancel={() => setEditItemsDialogOpen(false)}
				addon_id={addonIdToEdit}
				addon_choice_id={addonChoiceIdToEdit}
				firstDay={firstDay}
				lastDay={lastDay}
				daycareFirstDay={daycareFirstDay}
				daycareLastDay={daycareLastDay}
				hasEarlyDropoff={wizard_state['early_dropoff']}
				hasLatePickup={wizard_state['extended_pickup']}
			/>
			<RemoveFirstDayDaycareDialog
				open={removeFirstDayDaycareDialogOpen}
				onCancel={handleRemoveFirstDayDaycareDialogState}
				onDelete={handleRemoveFirstDayDaycare}
				hasMorningMealFirstDay={hasMorningMealFirstDay}
				hasMiddayMealFirstDay={hasMiddayMealFirstDay}
				hasEarlyDropoff={wizard_state['early_dropoff']}
			/>
			<RemoveLastDayDaycareDialog
				open={removeLastDayDaycareDialogOpen}
				onCancel={handleRemoveLastDayDaycareDialogState}
				onDelete={handleRemoveLastDayDaycare}
				hasEveningMealLastDay={hasEveningMealLastDay}
				hasMiddayMealLastDay={hasMiddayMealLastDay}
				hasLatePickup={wizard_state['extended_pickup']}
			/>
			<RemoveEarlyDropoffDialog
				open={removeEarlyDropoffDialogOpen}
				onCancel={handleRemoveEarlyDropoffDialogState}
				onDelete={handleRemoveEarlyDropoff}
				hasMorningMealFirstDay={hasMorningMealFirstDay}
				standardDropoffHours={daycare?.standard_dropoff_hours || ''}
			/>
			<RemoveLatePickupDialog
				open={removeLatePickupDialogOpen}
				onCancel={handleRemoveLatePickupDialogState}
				onDelete={handleRemoveLatePickup}
				hasEveningMealLastDay={hasEveningMealLastDay}
				standardPickupTime={daycare?.standard_pickup_time || ''}
			/>
			<PromoOnlyEntryDialog
				open={promoEntryDialogOpen}
				onCancel={handlePromoEntryDialogState}
				onComplete={handlePromoEntryDialogState}
				visit={wizard_state}
				service_targets={promoServiceTargets}
			/>
			<div className={classes.root}>
				{updating ? (
					<div className={classes.activityOverlay}>
						<CircularProgress />
					</div>
				) : null}
				<div className={classes.invoiceContainer}>
					<div className={`${classes.invoiceHeader} ${classes.emphasizedText}`}>
						Boarding Appointment Details
					</div>
					<FakeTable scrollable>
						<Header>
							<div>ITEM</div>
							<div>QTY</div>
							<div>PRICE</div>
							<div>UPCHARGE</div>
							<div>AMOUNT</div>
							<div>{NON_BREAKING_SPACE}</div>
						</Header>
						{[
							...daycareLineItems,
							...daycareCreditBankLineItem,
							...boardingServiceLineItem,
							...kennelOptionsLineItems,
							...holidaySurchargeLineItems,
							...addOnlineItems,
							...pickupDropoffLineItems,
							...foodScheduleLineItems,
							// ...addOnBankLineItems,
							...appliedPromoAndAddonBankLineItems,
							...availableAccountCreditLineItems
						].map(
							(
								{
									description,
									formattedQuantity,
									formattedPrice,
									formattedUpcharge,
									formattedTotalPrice,
									options,
									tooltipDescription
								},
								i
							) => {
								return (
									<Row key={`row-${i}`}>
										<div>{description}</div>
										<div>{formattedQuantity}</div>
										<div>{formattedPrice}</div>
										<div>{formattedUpcharge}</div>
										<div>{formattedTotalPrice}</div>
										{options ? (
											<MoreMenu description={tooltipDescription} options={options} />
										) : (
											<div>{NON_BREAKING_SPACE}</div>
										)}
									</Row>
								)
							}
						)}
					</FakeTable>
					<div className={classes.spacer} />
					<div className={classes.divider}>
						<div />
					</div>
					<FakeTable>
						{depositRequired ? (
							depositLineItems.map(({ description, formattedTotalPrice }, i) => {
								return (
									<Row key={`deposit-totals-${i}`}>
										<div className={i === 0 ? classes.emphasizedText : ''}>{description}</div>
										<div>{NON_BREAKING_SPACE}</div>
										<div>{NON_BREAKING_SPACE}</div>
										<div>{NON_BREAKING_SPACE}</div>
										<div className={i === 0 ? classes.emphasizedText : ''}>
											{formattedTotalPrice}
										</div>
										<div>{NON_BREAKING_SPACE}</div>
									</Row>
								)
							})
						) : (
							<Row>
								<div className={classes.emphasizedText}>Total Price</div>
								<div>{NON_BREAKING_SPACE}</div>
								<div>{NON_BREAKING_SPACE}</div>
								<div>{NON_BREAKING_SPACE}</div>
								<div className={classes.emphasizedText}>
									{wizard_state['applied_promo'] !== null || totalCreditAvailable > 0
										? formatNumsToDollars(totalPriceWithPromo)
										: formatNumsToDollars(totalPrice)}
								</div>
								<div>{NON_BREAKING_SPACE}</div>
							</Row>
						)}
					</FakeTable>
					<div>
						<Button
							onClick={handlePromoEntryDialogState}
							className={classes.addPromoButton}
							disabled={wizard_state['applied_promo'] !== null}
							variant="text">
							Add Promo
						</Button>
					</div>
				</div>
				<div className={classes.actionsContainer}>
					<DifferentFacilityBookingWarning
						facility={facility}
						defaultFacilityId={household?.default_facility_id}
						style={{ marginBottom: 16 }}
					/>
					<NoFacilityCardWarning
						facilityName={facilityCardStatus.facilityName || facility?.name}
						hasPaymentMethod={facilityCardStatus.hasPaymentMethod}
						loading={facilityCardStatus.loading}
						style={{ marginBottom: 16 }}
					/>
					<PaymentMethodCard
						raised={false}
						household={household}
						facility={facility}
						paymentMethods={facilityCardStatus.paymentMethods}
						onCardSaved={facilityCardStatus.refresh}
						makeResponsive
					/>
					<div className={classes.actionButtonContainer}>
						<MainButton
							onClick={handleOnConfirmAndBook}
							disabled={facilityCardStatus.loading || !hasCardAtThisFacility}
							variant="contained"
							label="Confirm & Book"
						/>
						<div className={classes.disclaimerText}>
							By clicking "Confirm & Book," I agree that I have notified the client of the quoted
							price, and
							<ButtonBase
								className={classes.inlineButtons}
								onClick={handleCancellationPolicyDialogState}>
								cancelation policy
							</ButtonBase>
							.
						</div>
						{showBankCreditDisclaimer ? (
							<StyledAlert
								type="info"
								text="Please note the invoice total may be adjusted if the client's daycare bank balance drops, their account credit is used or their enrichment bank is used prior to the completion of this visit."
								style={{ marginTop: 8 }}
							/>
						) : null}
					</div>
				</div>
			</div>
		</SectionFormContainer>
	)
}

export default ServiceSummaryAndPayment
